import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import * as React from 'react';

import { Badge } from './components/ui/badge.js';
import { cn } from './lib/utils.js';

// eslint-disable-next-line react-refresh/only-export-components
export const chipVariants = cva('', {
  variants: {
    variant: {
      default: '',
      secondary: '',
      destructive: '',
      outline: '',
      ghost: '',
      link: '',
      success:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 [a]:hover:bg-emerald-200 dark:[a]:hover:bg-emerald-900',
      warning:
        'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 [a]:hover:bg-amber-200 dark:[a]:hover:bg-amber-900',
      // Light-blue, informational -- distinct from warning/success: it names something scheduled
      // to happen, not a problem or a confirmation. Added for the price-sheet's scheduled-change
      // indicator.
      info: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300 [a]:hover:bg-sky-200 dark:[a]:hover:bg-sky-900',
      // Work is under way right now — distinct from `info`, which marks a state that is merely
      // known rather than moving.
      active:
        'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 [a]:hover:bg-violet-200 dark:[a]:hover:bg-violet-900',
      // Waiting on someone outside this screen, as distinct from `warning`, which asks the reader
      // to act.
      pending:
        'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950 dark:text-fuchsia-300 [a]:hover:bg-fuchsia-200 dark:[a]:hover:bg-fuchsia-900',
      // Part of the work is done and the rest is outstanding, as distinct from `active`'s "under way".
      partial:
        'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 [a]:hover:bg-blue-200 dark:[a]:hover:bg-blue-900',
      // Promised but past its date, as distinct from `warning`'s "act on this" and `destructive`'s "missing".
      overdue:
        'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 [a]:hover:bg-orange-200 dark:[a]:hover:bg-orange-900',
      // A signed figure that went up, as distinct from `success`, which confirms an outcome.
      increase:
        'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900 [a]:hover:bg-emerald-100 dark:[a]:hover:bg-emerald-900',
      // A signed figure that went down, as distinct from `destructive`, which names a failure.
      decrease:
        'bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900 [a]:hover:bg-rose-100 dark:[a]:hover:bg-rose-900',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'link';

/** One list; a variant added only to the union but missed in the `||` chain would silently lose its colour. */
const CHIP_ONLY_VARIANTS = [
  'success',
  'warning',
  'info',
  'active',
  'pending',
  'partial',
  'overdue',
  'increase',
  'decrease',
] as const;
type ChipOnlyVariant = (typeof CHIP_ONLY_VARIANTS)[number];
type ChipVariant = BadgeVariant | ChipOnlyVariant;

// The vendor Badge's text-destructive is 3.99:1 on its light tint; the dark pair already passes.
const DESTRUCTIVE_TEXT_CLASSES = 'text-red-700 dark:text-destructive';

function isChipOnlyVariant(variant: ChipVariant): variant is ChipOnlyVariant {
  return (CHIP_ONLY_VARIANTS as readonly string[]).includes(variant);
}

interface ChipProps
  extends Omit<React.ComponentProps<typeof Badge>, 'variant'>, VariantProps<typeof chipVariants> {
  variant?: ChipVariant;
  /** Accessible label for the dismiss button. Renders the button only when both this and `onDismiss` are set. Ignored when `asChild` is true — Badge's `asChild` requires a single child element. */
  dismissLabel?: string;
  onDismiss?: () => void;
}

export function DimeChip({
  className,
  variant = 'default',
  dismissLabel,
  onDismiss,
  children,
  asChild,
  ...props
}: Readonly<ChipProps>) {
  const isChipOnly = isChipOnlyVariant(variant);
  const showDismiss = !asChild && dismissLabel != null && onDismiss != null;

  // Badge's `asChild` (Radix Slot) requires exactly one child element to clone props onto — an
  // extra Fragment wrapper around it breaks that, so `children` only gets wrapped when the
  // dismiss button is actually being added.
  const content = showDismiss ? (
    <>
      {children}
      <button
        type="button"
        className="rounded-sm p-0.5 hover:bg-current/15"
        aria-label={dismissLabel}
        onClick={onDismiss}
      >
        <XIcon className="size-3" aria-hidden />
      </button>
    </>
  ) : (
    children
  );

  if (isChipOnly) {
    return (
      <Badge
        data-slot="chip"
        data-variant={variant}
        variant="outline"
        className={cn(
          chipVariants({ variant }),
          'border-transparent',
          showDismiss && 'gap-1 pr-1',
          className,
        )}
        asChild={asChild}
        {...props}
      >
        {content}
      </Badge>
    );
  }

  return (
    <Badge
      data-slot="chip"
      data-variant={variant}
      variant={variant}
      className={cn(
        variant === 'destructive' && DESTRUCTIVE_TEXT_CLASSES,
        showDismiss && 'gap-1 pr-1',
        className,
      )}
      asChild={asChild}
      {...props}
    >
      {content}
    </Badge>
  );
}
